// src/pages/AdminPartnersList.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  CardActions,
  Button,
  TextField,
  CircularProgress,
  Grid,
  Paper,
  InputAdornment,
  Avatar,
  Stack,
  Chip,
  Divider,
  IconButton,
  alpha,
  useTheme,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BusinessIcon from '@mui/icons-material/Business';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import { db } from '../contexts/firebase/config';
import { collection, onSnapshot } from 'firebase/firestore';

function initials(name) {
  if (!name) return 'P';
  const parts = String(name).trim().split(' ');
  const a = parts[0]?.[0] || 'P';
  const b = parts[1]?.[0] || '';
  return (a + b).toUpperCase();
}

export default function AdminPartnersList() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const colors = {
    accent: theme.palette.primary.main,
    surface: isDark ? 'rgba(2,6,23,0.72)' : 'rgba(255,255,255,0.72)',
    card: isDark ? 'rgba(15,23,42,0.62)' : 'rgba(255,255,255,0.74)',
    cardStrong: isDark ? 'rgba(15,23,42,0.92)' : 'rgba(255,255,255,0.94)',
    border: isDark ? 'rgba(148,163,184,0.18)' : 'rgba(15,23,42,0.09)',
    text: theme.palette.text.primary,
    muted: theme.palette.text.secondary,
    hero: isDark ? 'rgba(2,6,23,0.86)' : 'rgba(15,23,42,0.94)',
  };

  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setLoading(true);
    const unsub = onSnapshot(
      collection(db, 'empresas'),
      snap => {
        setPartners(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      () => setLoading(false)
    );
    return unsub;
  }, []);

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return partners;
    return partners.filter(
      p =>
        p.nomeEmpresa?.toLowerCase().includes(term) ||
        p.email?.toLowerCase().includes(term) ||
        String(p.nif || '').includes(term)
    );
  }, [partners, searchTerm]);

  const StatPill = ({ label, value }) => (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        px: 1.8,
        py: 1.45,
        minWidth: { xs: '100%', sm: 180 },
        borderRadius: '24px',
        bgcolor: 'rgba(255,255,255,0.09)',
        border: '1px solid rgba(255,255,255,0.18)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        '&:after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(135deg, rgba(255,255,255,0.14), transparent 48%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.62)', fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography sx={{ mt: 0.4, fontSize: '1.55rem', fontWeight: 950, color: '#ffffff', lineHeight: 1.05, letterSpacing: '-0.05em' }}>
        {value}
      </Typography>
    </Paper>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        px: { xs: 1.25, sm: 2.5, md: 3.5 },
        py: { xs: 2, md: 3.5 },
        background: isDark
          ? 'linear-gradient(135deg, #020617 0%, #07111f 42%, #101827 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 42%, #fdf2f8 100%)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: isDark
            ? `radial-gradient(760px 420px at 12% 0%, ${alpha(colors.accent, 0.34)}, transparent 64%), radial-gradient(760px 460px at 94% 4%, ${alpha(theme.palette.secondary?.main || '#a855f7', 0.26)}, transparent 58%), radial-gradient(680px 420px at 50% 112%, ${alpha(theme.palette.success.main, 0.18)}, transparent 62%)`
            : `radial-gradient(760px 420px at 12% 0%, ${alpha(colors.accent, 0.22)}, transparent 64%), radial-gradient(760px 460px at 94% 4%, ${alpha(theme.palette.secondary?.main || '#a855f7', 0.18)}, transparent 58%), radial-gradient(680px 420px at 50% 112%, ${alpha(theme.palette.success.main, 0.12)}, transparent 62%)`,
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: isDark ? 0.22 : 0.36,
          pointerEvents: 'none',
          backgroundImage:
            'linear-gradient(rgba(148,163,184,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.18) 1px, transparent 1px)',
          backgroundSize: '42px 42px',
          maskImage: 'linear-gradient(to bottom, black, transparent 74%)',
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          width: 360,
          height: 360,
          right: { xs: -220, md: -80 },
          top: 180,
          borderRadius: '50%',
          pointerEvents: 'none',
          background: `radial-gradient(circle, ${alpha(colors.accent, isDark ? 0.18 : 0.12)}, transparent 68%)`,
          filter: 'blur(12px)',
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 1, width: '100%' }}>
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            borderRadius: { xs: '30px', md: '46px' },
            border: `1px solid ${colors.border}`,
            bgcolor: colors.surface,
            backdropFilter: 'blur(26px)',
            WebkitBackdropFilter: 'blur(26px)',
            boxShadow: isDark
              ? '0 34px 110px rgba(0,0,0,0.48)'
              : '0 30px 90px rgba(15,23,42,0.13)',
            p: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              width: '100%',
              mb: 3,
              p: { xs: 2.75, md: 4.5 },
              borderRadius: { xs: '30px', md: '38px' },
              bgcolor: colors.hero,
              color: 'common.white',
              border: '1px solid rgba(255,255,255,0.14)',
              boxShadow: isDark
                ? `0 28px 86px ${alpha(colors.accent, 0.14)}, 0 28px 72px rgba(0,0,0,0.44)`
                : '0 28px 86px rgba(15,23,42,0.24)',
              '&:before': {
                content: '""',
                position: 'absolute',
                width: 620,
                height: 620,
                right: -180,
                top: -260,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha(colors.accent, 0.62)} 0%, ${alpha(theme.palette.success.main, 0.18)} 38%, transparent 68%)`,
              },
              '&:after': {
                content: '""',
                position: 'absolute',
                left: -90,
                bottom: -150,
                width: 460,
                height: 460,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha(theme.palette.secondary?.main || '#a855f7', 0.28)} 0%, transparent 66%)`,
              },
            }}
          >
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={3}
              justifyContent="space-between"
              alignItems="stretch"
              sx={{ position: 'relative', zIndex: 1 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 46,
                      height: 46,
                      borderRadius: '18px',
                      bgcolor: 'rgba(255,255,255,0.11)',
                      color: 'common.white',
                      border: '1px solid rgba(255,255,255,0.18)',
                      boxShadow: `0 16px 36px ${alpha(colors.accent, 0.22)}`,
                    }}
                  >
                    <AdminPanelSettingsRoundedIcon fontSize="small" />
                  </Avatar>

                  <Typography
                    variant="overline"
                    sx={{
                      fontSize: 12,
                      fontWeight: 950,
                      letterSpacing: '0.2em',
                      color: 'rgba(255,255,255,0.64)',
                    }}
                  >
                    Painel de Administração
                  </Typography>
                </Stack>

                <Typography
                  sx={{
                    fontSize: { xs: '2.35rem', sm: '3.2rem', md: '4.25rem' },
                    fontWeight: 950,
                    lineHeight: 0.92,
                    letterSpacing: '-0.085em',
                    color: '#ffffff',
                    textShadow: `0 20px 60px ${alpha(colors.accent, 0.34)}`,
                  }}
                >
                  Parceiros
                </Typography>

                <Typography
                  sx={{
                    mt: 1.8,
                    maxWidth: 850,
                    color: 'rgba(255,255,255,0.70)',
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    lineHeight: 1.75,
                  }}
                >
                  Pesquisa, consulta e gere todos os parceiros registados na plataforma.
                </Typography>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2.4 }}>
                  <Chip
                    icon={<BusinessIcon />}
                    label="Gestão de parceiros"
                    sx={{
                      height: 36,
                      borderRadius: '999px',
                      color: '#fff',
                      fontWeight: 900,
                      bgcolor: 'rgba(255,255,255,0.10)',
                      border: '1px solid rgba(255,255,255,0.16)',
                      '& .MuiChip-icon': { color: 'rgba(255,255,255,0.82)' },
                    }}
                  />
                  <Chip
                    label="Área administrativa"
                    sx={{
                      height: 36,
                      borderRadius: '999px',
                      color: '#fff',
                      fontWeight: 900,
                      bgcolor: alpha(colors.accent, 0.24),
                      border: '1px solid rgba(255,255,255,0.16)',
                    }}
                  />
                </Stack>
              </Box>

              <Stack
                direction={{ xs: 'column', sm: 'row', lg: 'column' }}
                spacing={1.25}
                sx={{ width: { xs: '100%', lg: 268 }, flexShrink: 0 }}
              >
                <StatPill label="Total de parceiros" value={loading ? '…' : partners.length} />
                <StatPill label="Resultados visíveis" value={loading ? '…' : filtered.length} />
              </Stack>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              mb: 3,
              p: { xs: 2.25, md: 2.8 },
              borderRadius: { xs: '28px', md: '34px' },
              border: `1px solid ${colors.border}`,
              bgcolor: colors.card,
              backdropFilter: 'blur(18px)',
              WebkitBackdropFilter: 'blur(18px)',
              boxShadow: isDark ? '0 24px 70px rgba(0,0,0,0.28)' : '0 24px 70px rgba(15,23,42,0.08)',
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between"
              spacing={2}
              sx={{ mb: 2.2 }}
            >
              <Stack direction="row" spacing={1.45} alignItems="center">
                <Avatar
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: '19px',
                    bgcolor: alpha(colors.accent, isDark ? 0.22 : 0.12),
                    color: colors.accent,
                    border: `1px solid ${alpha(colors.accent, 0.22)}`,
                  }}
                >
                  <StorefrontRoundedIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={950} sx={{ color: colors.text, letterSpacing: '-0.035em' }}>
                    Lista de parceiros
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {filtered.length} de {partners.length} parceiro(s).
                  </Typography>
                </Box>
              </Stack>

              <Button
                variant="contained"
                component={RouterLink}
                to="/admin/parceiros/new"
                startIcon={<PersonAddIcon />}
                sx={{
                  alignSelf: { xs: 'stretch', md: 'center' },
                  height: 48,
                  borderRadius: '18px',
                  px: 2.5,
                  fontWeight: 950,
                  textTransform: 'none',
                  boxShadow: `0 18px 42px ${alpha(colors.accent, 0.28)}`,
                  background: `linear-gradient(135deg, ${colors.accent}, ${alpha(colors.accent, 0.72)})`,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 24px 54px ${alpha(colors.accent, 0.34)}`,
                  },
                }}
              >
                Novo Parceiro
              </Button>
            </Stack>

            <TextField
              placeholder="Pesquisar por nome, email ou NIF…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              fullWidth
              size="medium"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearRoundedIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  minHeight: 58,
                  borderRadius: '22px',
                  bgcolor: colors.cardStrong,
                  boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.06)' : '0 12px 30px rgba(15,23,42,0.04)',
                  '& fieldset': { borderColor: colors.border },
                  '&:hover fieldset': { borderColor: alpha(colors.accent, 0.42) },
                  '&.Mui-focused fieldset': { borderColor: colors.accent, borderWidth: 1 },
                },
              }}
            />
          </Paper>

          {loading ? (
            <Paper
              elevation={0}
              sx={{
                p: 7,
                borderRadius: '34px',
                border: `1px solid ${colors.border}`,
                bgcolor: colors.card,
                textAlign: 'center',
                backdropFilter: 'blur(18px)',
                WebkitBackdropFilter: 'blur(18px)',
              }}
            >
              <CircularProgress />
            </Paper>
          ) : filtered.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 7 },
                borderRadius: '34px',
                textAlign: 'center',
                border: `1px dashed ${alpha(colors.accent, 0.34)}`,
                bgcolor: colors.card,
                backdropFilter: 'blur(18px)',
                WebkitBackdropFilter: 'blur(18px)',
              }}
            >
              <Avatar
                sx={{
                  width: 82,
                  height: 82,
                  mx: 'auto',
                  mb: 2,
                  borderRadius: '30px',
                  bgcolor: alpha(colors.accent, isDark ? 0.22 : 0.12),
                  color: colors.accent,
                  boxShadow: `0 20px 48px ${alpha(colors.accent, 0.18)}`,
                }}
              >
                <SearchOffRoundedIcon fontSize="large" />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 950, color: colors.text }} gutterBottom>
                Nenhum parceiro encontrado
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Ajusta a pesquisa ou cria um novo parceiro.
              </Typography>
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                component={RouterLink}
                to="/admin/parceiros/new"
                sx={{ borderRadius: '18px', textTransform: 'none', fontWeight: 900 }}
              >
                Criar Parceiro
              </Button>
            </Paper>
          ) : (
            <Grid container spacing={2.5}>
              {filtered.map(p => (
                <Grid item xs={12} sm={6} lg={4} xl={3} key={p.id}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      borderRadius: '32px',
                      border: `1px solid ${colors.border}`,
                      bgcolor: colors.card,
                      overflow: 'hidden',
                      backdropFilter: 'blur(18px)',
                      WebkitBackdropFilter: 'blur(18px)',
                      boxShadow: isDark
                        ? '0 20px 58px rgba(0,0,0,0.30)'
                        : '0 18px 52px rgba(15,23,42,0.08)',
                      transition: 'all 0.24s ease',
                      '&:before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        background: `linear-gradient(135deg, ${alpha(colors.accent, isDark ? 0.16 : 0.09)}, transparent 42%)`,
                        opacity: 0,
                        transition: 'opacity 0.24s ease',
                        pointerEvents: 'none',
                      },
                      '&:hover': {
                        transform: 'translateY(-7px)',
                        borderColor: alpha(colors.accent, 0.38),
                        boxShadow: isDark
                          ? `0 30px 86px rgba(0,0,0,0.44), 0 0 0 1px ${alpha(colors.accent, 0.1)}`
                          : '0 30px 80px rgba(15,23,42,0.14)',
                      },
                      '&:hover:before': {
                        opacity: 1,
                      },
                    }}
                  >
                    <CardActionArea
                      component={RouterLink}
                      to={`/admin/empresa/${p.id}/info`}
                      sx={{ flexGrow: 1, alignItems: 'stretch', height: '100%', position: 'relative', zIndex: 1 }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Stack direction="row" spacing={1.6} alignItems="center" sx={{ mb: 1.8 }}>
                          <Avatar
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: '22px',
                              bgcolor: alpha(colors.accent, isDark ? 0.22 : 0.12),
                              color: colors.accent,
                              fontWeight: 950,
                              border: `1px solid ${alpha(colors.accent, 0.24)}`,
                              boxShadow: `0 18px 42px ${alpha(colors.accent, isDark ? 0.16 : 0.12)}`,
                            }}
                          >
                            {initials(p.nomeEmpresa)}
                          </Avatar>

                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 950, color: colors.text, letterSpacing: '-0.03em' }}>
                              {p.nomeEmpresa || '—'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {p.email || '—'}
                            </Typography>
                          </Box>
                        </Stack>

                        <Divider sx={{ my: 1.7, borderColor: colors.border }} />

                        <Stack spacing={1.15}>
                          <Chip
                            icon={<BadgeRoundedIcon />}
                            size="small"
                            label={`NIF: ${p.nif || '—'}`}
                            sx={{
                              width: 'fit-content',
                              height: 32,
                              borderRadius: '999px',
                              fontWeight: 900,
                              bgcolor: isDark ? 'rgba(255,255,255,0.055)' : 'rgba(15,23,42,0.045)',
                              border: `1px solid ${colors.border}`,
                              '& .MuiChip-icon': { color: colors.muted },
                            }}
                          />

                          <Chip
                            icon={<EmailRoundedIcon />}
                            size="small"
                            label={p.email || 'Sem email'}
                            sx={{
                              maxWidth: '100%',
                              height: 32,
                              borderRadius: '999px',
                              fontWeight: 850,
                              bgcolor: alpha(colors.accent, isDark ? 0.16 : 0.08),
                              color: colors.accent,
                              border: `1px solid ${alpha(colors.accent, 0.16)}`,
                              '& .MuiChip-icon': { color: colors.accent },
                            }}
                          />
                        </Stack>
                      </CardContent>
                    </CardActionArea>

                    <CardActions sx={{ justifyContent: 'space-between', p: 2.3, pt: 0, position: 'relative', zIndex: 1 }}>
                      <Chip
                        size="small"
                        label="Parceiro"
                        sx={{
                          height: 31,
                          borderRadius: '999px',
                          fontWeight: 950,
                          bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.05)',
                          border: `1px solid ${colors.border}`,
                        }}
                      />

                      <Button
                        component={RouterLink}
                        to={`/admin/empresa/${p.id}/info`}
                        size="small"
                        endIcon={<ArrowForwardRoundedIcon />}
                        sx={{
                          borderRadius: '999px',
                          textTransform: 'none',
                          fontWeight: 950,
                          color: colors.accent,
                          px: 1.35,
                          '&:hover': {
                            bgcolor: alpha(colors.accent, isDark ? 0.16 : 0.08),
                          },
                        }}
                      >
                        Ver
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
